import React, { useEffect, useState } from 'react';
import { Clock, AlertTriangle, XCircle, Lock } from 'lucide-react';

interface AuthErrorProps {
  type?: string;
}

export const AuthError: React.FC<AuthErrorProps> = ({ type }) => {
  const [errorType, setErrorType] = useState<string>('general');

  useEffect(() => {
    // Get error type from URL params if not provided as prop
    const urlParams = new URLSearchParams(window.location.search);
    const urlErrorType = urlParams.get('type');
    
    if (type) {
      setErrorType(type);
    } else if (urlErrorType) {
      setErrorType(urlErrorType);
    }
  }, [type]);

  const getErrorContent = () => {
    switch (errorType) {
      case 'external_user':
        return {
          title: 'Access Restricted',
          message: 'You need a UniFirst account to access this application.',
          details: 'This application is only available to UniFirst employees and authorized users. Please contact your system administrator if you believe you should have access.',
          icon: 'lock'
        };
      case 'expired':
        return {
          title: 'Session Expired',
          message: 'Your login session has expired.',
          details: 'Please sign in again to continue using the application.',
          icon: 'clock'
        };
      case 'permission':
        return {
          title: 'Insufficient Permissions',
          message: 'You do not have permission to access this application.',
          details: 'Contact your administrator to request access or verify your account permissions.',
          icon: 'alert'
        };
      default:
        return {
          title: 'Authentication Error',
          message: 'We encountered an issue during sign-in.',
          details: 'Please try signing in again. If the problem persists, contact your system administrator.',
          icon: 'error'
        };
    }
  };

  const renderIcon = () => {
    const iconClass = "h-24 w-24 text-red-500 mx-auto";
    switch (errorContent.icon) {
      case 'lock':
        return <Lock className={iconClass} />;
      case 'clock':
        return <Clock className={iconClass} />;
      case 'alert':
        return <AlertTriangle className={iconClass} />;
      case 'error':
        return <XCircle className={iconClass} />;
      default:
        return <XCircle className={iconClass} />;
    }
  };

  const errorContent = getErrorContent();

  const handleRetry = () => {
    // Clear any auth-related localStorage items
    localStorage.removeItem('oidc_auth_success');
    localStorage.removeItem('oidc_auth_timestamp');
    sessionStorage.clear();
    
    // Navigate back to login
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    // Create a pre-filled support email
    const subject = encodeURIComponent(`Scout Central Access Issue - ${errorContent.title}`);
    const body = encodeURIComponent(`
Hello,

I encountered an authentication issue while trying to access Scout Central:

Error Type: ${errorType}
Message: ${errorContent.message}
Time: ${new Date().toISOString()}
URL: ${window.location.href}

Please assist me with gaining access to the application.

Thank you,
`);
    
    window.location.href = `mailto:it-support@unifirst.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo or branding area */}
        <div className="text-center">
          <div className="mb-4">{renderIcon()}</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {errorContent.title}
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center space-y-6">
            {/* Main error message */}
            <div>
              <p className="text-lg font-medium text-gray-900 mb-3">
                {errorContent.message}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                {errorContent.details}
              </p>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Try Again
              </button>
              
              <button
                onClick={handleContactSupport}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Contact Support
              </button>
            </div>

            {/* Additional help text */}
            <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
              <p>
                Need immediate assistance? Contact IT Support at{' '}
                <a href="tel:+1-800-xxx-xxxx" className="text-blue-600 hover:text-blue-500">
                  (800) XXX-XXXX
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Optional: Development info (only show in dev mode) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="text-xs text-yellow-800">
              <strong>Dev Info:</strong>
              <br />Error Type: {errorType}
              <br />URL: {window.location.href}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthError;
